import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useTheme } from '@mui/material/styles';
import { smsInsuranceTypes, smsMonths, SmsSend, smsLog, smspatients, smsReligions } from '../BaseURL';
import { checkSmsBalance } from '../Message';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const smsPages = (len) => (len === 0 ? 1 : Math.ceil(len / 160));
const charsLeft = (len) => smsPages(len) * 160 - len;

const insuranceBadge = (type = '', isDark = false) => {
  const t = type.toLowerCase();
  if (t.includes('nhia') || t.includes('nhis')) {
    return isDark
      ? { bg: 'rgba(21,128,61,0.18)', color: '#4ade80', border: 'rgba(74,222,128,0.30)' }
      : { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' };
  }
  if (t.includes('non')) {
    return isDark
      ? { bg: 'rgba(217,119,6,0.18)', color: '#fbbf24', border: 'rgba(251,191,36,0.30)' }
      : { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
  }
  return isDark
    ? { bg: 'rgba(25,118,210,0.18)', color: '#60a5fa', border: 'rgba(96,165,250,0.30)' }
    : { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' };
};

const avatarColor = (name = '') => {
  const colors = ['#11998e', '#0ea5e9', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444'];
  return colors[(name.charCodeAt(0) || 0) % colors.length];
};

const TEMPLATES = [
  { label: '📅 Appointment', msg: 'Dear patient, you have an upcoming appointment at our facility. Please call us to confirm your visit. Thank you.' },
  { label: '💊 Medication',  msg: 'This is a reminder to collect your medication from our pharmacy. Please bring your patient ID. Thank you.' },
  { label: '🧪 Results Ready', msg: 'Your laboratory/scan results are ready for collection. Please visit us at your earliest convenience.' },
  { label: '❤️ Follow-Up',  msg: 'We hope you are recovering well. Please schedule a follow-up visit with your doctor when convenient.' },
];

// ════════════════════════════════════════════════════════════════════════════
export default function SmsMessage() {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [patients,   setPatients]   = useState([]);
  const [insurTypes, setInsurTypes] = useState([]);
  const [months,     setMonths]     = useState([]);
  const [religions,  setReligions]  = useState([]);
  const [search,        setSearch]        = useState('');   // live input (not yet submitted)
  const [appliedSearch, setAppliedSearch] = useState('');   // committed on button click / Enter
  const [fInsur,     setFInsur]     = useState('');
  const [fMonth,     setFMonth]     = useState('');
  const [fReligion,  setFReligion]  = useState('');
  const [selected,   setSelected]   = useState([]);
  const [message,    setMessage]    = useState('');
  const [loading,    setLoading]    = useState(false);
  const [sending,    setSending]    = useState(false);
  const [page,       setPage]       = useState(0);
  const [toast,      setToast]      = useState(null);

  // ── Patient Quick-Search State ──
  const [pSearch,     setPSearch]     = useState('');
  const [pDropdown,   setPDropdown]   = useState(false);
  const pSearchRef = useRef();

  // ── SMS Balance State ──
  const [balanceModal,   setBalanceModal]   = useState(false);
  const [balanceData,    setBalanceData]    = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError,   setBalanceError]   = useState('');

  const ROWS = 8;
  const textRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const [ir, mr] = await Promise.all([
          axios.get(smsInsuranceTypes),
          axios.get(smsMonths),
        ]);
        if (ir.data.success) setInsurTypes(ir.data.data);
        if (mr.data.success) setMonths(mr.data.data);

        // Fetch religions from patient table
        try {
          const rr = await axios.get(smsReligions);
          if (rr.data.success) setReligions(rr.data.data);
        } catch {
          console.error('Failed to load religions');
        }
      } catch {}
    })();
  }, []);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    setPage(0);
    try {
      const res = await axios.get(smspatients, {
        params: {
          search:    appliedSearch.trim() || undefined,  // uses committed search only
          insurance: fInsur.trim()        || undefined,
          month:     fMonth.trim()        || undefined,
          religion:  fReligion.trim()     || undefined,
        },
      });
      if (res.data.success) setPatients(res.data.data);
    } catch {}
    finally { setLoading(false); }
  }, [appliedSearch, fInsur, fMonth, fReligion]);  // dropdowns still trigger reload instantly

  // Runs when dropdowns change or search is committed — NOT on every keystroke
  useEffect(() => {
    const t = setTimeout(loadPatients, 200);
    return () => clearTimeout(t);
  }, [loadPatients]);

  // Commit the search (button click or Enter key)
  const commitSearch = () => setAppliedSearch(search);

  const isSelected = (id) => selected.some(s => s.id === id);
  const toggle     = (p)  => setSelected(prev => isSelected(p.id) ? prev.filter(s => s.id !== p.id) : [...prev, p]);
  const selectPage = ()   => {
    const visible = patients.slice(page * ROWS, page * ROWS + ROWS);
    setSelected(prev => { const next = [...prev]; visible.forEach(p => { if (!isSelected(p.id)) next.push(p); }); return next; });
  };
  const selectAll  = ()   => setSelected([...patients]);
  const clearAll   = ()   => setSelected([]);
  const resetFilters = () => { setSearch(''); setAppliedSearch(''); setFInsur(''); setFMonth(''); setFReligion(''); };

  // ── Check SMS Balance — calls imported utility from Message.js ──
  const handleCheckBalance = async () => {
    setBalanceModal(true);
    setBalanceLoading(true);
    setBalanceError('');
    setBalanceData(null);

    const result = await checkSmsBalance();   // ← uses Message.js export

    if (result.success) {
      setBalanceData(result.data);
    } else {
      setBalanceError(result.error);
    }
    setBalanceLoading(false);
  };

  const handleSend = async () => {
    if (!selected.length) return showToast('Select at least one patient', false);
    if (!message.trim())  return showToast('Please type a message first', false);

    const ok = await Swal.fire({
      title: 'Send SMS?',
      html: `<b>${selected.length}</b> recipient${selected.length > 1 ? 's' : ''} · <b>${smsPages(message.length)}</b> SMS page${smsPages(message.length) > 1 ? 's' : ''} each`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send Now',
      confirmButtonColor: '#11998e',
    });
    if (!ok.isConfirmed) return;

    setSending(true);
    try {
      const res = await axios.post(SmsSend, { recipients: selected, message });
      if (res.data.success) {
        showToast(res.data.message, true);
        setMessage('');
        setSelected([]);
      } else {
        showToast(res.data.message, false);
      }
    } catch { showToast('Failed to send SMS', false); }
    finally { setSending(false); }
  };

  const showToast = (msg, ok) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const totalPages = Math.ceil(patients.length / ROWS);
  const slice      = patients.slice(page * ROWS, page * ROWS + ROWS);
  const msgLen     = message.length;

  // patients matching the quick-search that are not yet selected
  const pSuggestions = pSearch.trim().length >= 1
    ? patients.filter(p =>
        !isSelected(p.id) &&
        (
          (p.fullName || '').toLowerCase().includes(pSearch.toLowerCase()) ||
          String(p.id).includes(pSearch) ||
          (p.tel || '').includes(pSearch)
        )
      ).slice(0, 8)
    : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --teal:       #11998e;
          --teal-dark:  #0d7a71;
          --teal-grad:  linear-gradient(135deg, #11998e 0%, #0e8a7f 100%);
          ${isDark ? `
          --teal-light: rgba(17,153,142,0.14);
          --teal-mid:   rgba(17,153,142,0.40);
          --teal-soft:  rgba(17,153,142,0.08);
          --text-dark:  #FFFFFF;
          --text-mid:   #A7E8E2;
          --text-muted: rgba(255,255,255,0.55);
          --border:     rgba(255,255,255,0.10);
          --border-s:   rgba(255,255,255,0.06);
          --white:      #0E0E14;
          --bg:         #000000;
          --shadow:     0 2px 14px rgba(0,0,0,.35);
          --shadow-lg:  0 8px 32px rgba(0,0,0,.45);
          --row-alt:    rgba(255,255,255,0.03);
          --input-bg:   #14141C;
          ` : `
          --teal-light: #e6f7f6;
          --teal-mid:   #b2e8e5;
          --teal-soft:  #f0fbfa;
          --text-dark:  #0f2d2b;
          --text-mid:   #2d6b65;
          --text-muted: #64a09b;
          --border:     #c8e9e7;
          --border-s:   #e0f5f4;
          --white:      #ffffff;
          --bg:         #f4fafa;
          --shadow:     0 2px 14px rgba(17,153,142,.10);
          --shadow-lg:  0 8px 32px rgba(17,153,142,.18);
          --row-alt:    #f7fcfb;
          --input-bg:   #ffffff;
          `}
        }

        .sms-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: var(--bg);
          min-height: 100vh;
          color: var(--text-dark);
          padding: 28px 24px;
          display: flex;
          flex-direction: column;
          gap: 22px;
        }

        .sms-header {
          display: flex; align-items: center;
          justify-content: space-between;
          flex-wrap: wrap; gap: 14px;
        }
        .sms-header-left { display: flex; align-items: center; gap: 14px; }
        .sms-icon-box {
          width: 52px; height: 52px; border-radius: 14px;
          background: var(--teal-grad);
          display: flex; align-items: center; justify-content: center;
          font-size: 24px;
          box-shadow: 0 4px 18px rgba(17,153,142,.35);
        }
        .sms-title    { font-size: 21px; font-weight: 800; color: var(--text-dark); letter-spacing: -.4px; }
        .sms-subtitle { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
        .sms-header-right { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

        .balance-btn {
          display: flex; align-items: center; gap: 7px;
          background: var(--white);
          border: 2px solid var(--teal-mid);
          border-radius: 11px;
          color: ${isDark ? '#5eead4' : 'var(--teal-dark)'};
          cursor: pointer;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px; font-weight: 700;
          padding: 8px 16px;
          transition: all .2s;
          box-shadow: var(--shadow);
          white-space: nowrap;
        }
        .balance-btn:hover {
          background: var(--teal);
          border-color: var(--teal);
          color: white;
          box-shadow: 0 4px 18px rgba(17,153,142,.35);
          transform: translateY(-1px);
        }
        .balance-btn:active { transform: translateY(0); }

        .sms-stats { display: flex; gap: 10px; flex-wrap: wrap; }
        .stat-pill {
          background: var(--white); border: 1.5px solid var(--border);
          border-radius: 12px; padding: 8px 18px;
          display: flex; flex-direction: column; align-items: center;
          box-shadow: var(--shadow); min-width: 82px;
          transition: box-shadow .2s;
        }
        .stat-pill:hover { box-shadow: var(--shadow-lg); }
        .stat-pill-val   { font-weight: 800; font-size: 19px; font-family: 'JetBrains Mono', monospace; color: var(--teal); line-height: 1; }
        .stat-pill-label { color: var(--text-muted); font-size: 10px; font-weight: 600; letter-spacing: .5px; text-transform: uppercase; margin-top: 3px; }
        .stat-pill.pages .stat-pill-val { color: #f59e0b; }
        .stat-pill.chars .stat-pill-val { color: #6366f1; }

        .sms-body {
          display: grid;
          grid-template-columns: 1fr 390px;
          gap: 20px; align-items: start;
        }
        @media (max-width: 960px) { .sms-body { grid-template-columns: 1fr; } }

        .panel {
          background: var(--white);
          border: 1.5px solid var(--border);
          border-radius: 18px; overflow: hidden;
          box-shadow: var(--shadow);
        }
        .panel-header {
          padding: 13px 20px;
          border-bottom: 1.5px solid var(--border-s);
          display: flex; align-items: center; justify-content: space-between;
          background: var(--teal-soft);
        }
        .panel-title {
          font-size: 11px; font-weight: 700; color: var(--teal);
          text-transform: uppercase; letter-spacing: 1.1px;
        }

        .filter-bar {
          padding: 14px 20px;
          border-bottom: 1.5px solid var(--border-s);
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          gap: 10px;
          background: ${isDark ? 'rgba(255,255,255,0.02)' : '#fafffe'};
        }
        @media (max-width: 860px) { .filter-bar { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 540px) { .filter-bar { grid-template-columns: 1fr; } }

        .sms-input {
          width: 100%; background: var(--input-bg);
          border: 1.5px solid var(--border); border-radius: 9px;
          color: var(--text-dark); font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 500; padding: 8px 12px;
          outline: none; transition: border-color .2s, box-shadow .2s;
          appearance: none;
        }
        .sms-input:focus { border-color: var(--teal); box-shadow: 0 0 0 3px rgba(17,153,142,.20); }
        .sms-input::placeholder { color: var(--text-muted); }
        .sms-input option { background: var(--input-bg); color: var(--text-dark); }

        .bulk-bar {
          padding: 8px 20px;
          border-bottom: 1.5px solid var(--border-s);
          display: flex; align-items: center; justify-content: space-between;
          background: var(--teal-soft);
        }
        .bulk-bar-info { font-size: 12px; color: var(--text-muted); font-family: 'JetBrains Mono', monospace; }
        .bulk-btns { display: flex; gap: 6px; }
        .bulk-btn {
          background: var(--white); border: 1.5px solid var(--border);
          border-radius: 7px; color: var(--teal); cursor: pointer;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 11px; font-weight: 700; padding: 4px 12px;
          transition: all .2s;
        }
        .bulk-btn:hover        { background: var(--teal); color: white; border-color: var(--teal); box-shadow: 0 2px 8px rgba(17,153,142,.25); }
        .bulk-btn.danger:hover { background: #ef4444; border-color: #ef4444; color: white; }

        .patient-table { width: 100%; border-collapse: collapse; }
        .patient-table th {
          background: var(--teal-soft); color: var(--text-mid);
          font-size: 11px; font-weight: 700; letter-spacing: .7px;
          padding: 10px 18px; text-align: left; text-transform: uppercase;
          border-bottom: 1.5px solid var(--border-s);
        }
        .patient-row {
          border-bottom: 1px solid var(--border-s);
          cursor: pointer; transition: background .15s;
        }
        .patient-row:hover       { background: var(--teal-soft); }
        .patient-row.sel-row     { background: var(--teal-light); }
        .patient-row td          { padding: 10px 18px; font-size: 13px; vertical-align: middle; }

        .avatar {
          width: 34px; height: 34px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 13px; color: white; flex-shrink: 0;
        }
        .patient-name { font-weight: 700; color: var(--text-dark); font-size: 13px; }
        .patient-id   { font-size: 11px; color: var(--text-muted); font-family: 'JetBrains Mono', monospace; }
        .phone-val    { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--text-mid); font-weight: 500; }

        .ins-badge {
          display: inline-block; border-radius: 6px; border: 1.5px solid;
          font-size: 11px; font-weight: 700; padding: 2px 9px;
        }
        .rel-badge {
          display: inline-block; border-radius: 6px;
          font-size: 11px; font-weight: 600; padding: 2px 9px;
          background: ${isDark ? 'rgba(139,92,246,0.18)' : '#f3f0ff'};
          color: ${isDark ? '#c4b5fd' : '#6d28d9'};
          border: 1.5px solid ${isDark ? 'rgba(196,181,253,0.30)' : '#c4b5fd'};
        }

        .check-box {
          width: 16px; height: 16px; border-radius: 4px;
          cursor: pointer; accent-color: var(--teal); flex-shrink: 0;
        }

        .pagination {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 20px; border-top: 1.5px solid var(--border-s);
          background: var(--teal-soft);
        }
        .pag-info { font-size: 11px; color: var(--text-muted); font-family: 'JetBrains Mono', monospace; }
        .pag-btns { display: flex; gap: 4px; }
        .pag-btn {
          background: var(--white); border: 1.5px solid var(--border);
          border-radius: 7px; color: var(--text-mid);
          cursor: pointer; font-size: 12px; font-weight: 600; padding: 4px 10px;
          transition: all .15s;
        }
        .pag-btn:hover:not(:disabled) { border-color: var(--teal); color: var(--teal); background: var(--teal-light); }
        .pag-btn:disabled { opacity: .35; cursor: default; }
        .pag-btn.active   { background: var(--teal); border-color: var(--teal); color: white; box-shadow: 0 2px 8px rgba(17,153,142,.3); }

        .right-col { display: flex; flex-direction: column; gap: 16px; }

        .chips-area {
          padding: 14px 18px; min-height: 90px;
          display: flex; flex-wrap: wrap; gap: 7px; align-content: flex-start;
        }
        .chip {
          display: flex; align-items: center; gap: 5px;
          background: var(--teal-light); border: 1.5px solid var(--teal-mid);
          border-radius: 8px; color: ${isDark ? '#5eead4' : 'var(--teal-dark)'};
          font-size: 11px; font-weight: 700; padding: 3px 9px;
          animation: chipIn .18s ease;
        }
        @keyframes chipIn { from { opacity:0; transform:scale(.8); } to { opacity:1; transform:scale(1); } }
        .chip-x {
          background: none; border: none; color: var(--teal);
          cursor: pointer; font-size: 14px; padding: 0 0 0 2px;
          line-height: 1; transition: color .15s;
        }
        .chip-x:hover { color: #ef4444; }
        .chips-empty {
          font-size: 12px; color: var(--text-muted);
          width: 100%; text-align: center; padding: 14px 0; font-style: italic;
        }

        .compose-area { padding: 16px 18px; }
        .sms-textarea {
          width: 100%; background: var(--input-bg);
          border: 1.5px solid var(--border); border-radius: 11px;
          color: var(--text-dark); font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 14px; font-weight: 500; line-height: 1.65;
          padding: 13px 15px; resize: vertical; min-height: 130px;
          outline: none; transition: border-color .2s, box-shadow .2s; display: block;
        }
        .sms-textarea:focus { border-color: var(--teal); box-shadow: 0 0 0 3px rgba(17,153,142,.20); }
        .sms-textarea::placeholder { color: var(--text-muted); }

        .char-bar {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 8px; font-size: 11px; color: var(--text-muted);
          font-family: 'JetBrains Mono', monospace;
        }
        .char-bar .pages {
          background: var(--teal-light); border: 1.5px solid var(--teal-mid);
          border-radius: 6px; padding: 2px 9px; font-size: 10px;
          color: var(--teal); font-weight: 700;
        }
        .char-warn { color: #f59e0b !important; }

        .tpl-label {
          font-size: 10px; color: var(--text-muted); font-weight: 700;
          margin: 13px 0 7px; letter-spacing: .5px; text-transform: uppercase;
        }
        .tpl-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px; }
        .tpl-btn {
          background: var(--teal-soft); border: 1.5px solid var(--border);
          border-radius: 8px; color: ${isDark ? '#5eead4' : 'var(--teal-dark)'};
          cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 11px; font-weight: 600; padding: 5px 11px; transition: all .2s;
        }
        .tpl-btn:hover { background: var(--teal); border-color: var(--teal); color: white; box-shadow: 0 2px 8px rgba(17,153,142,.25); }

        .send-btn {
          width: 100%; background: var(--teal-grad);
          border: none; border-radius: 12px; color: white;
          cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 15px; font-weight: 800; padding: 14px;
          transition: all .2s; box-shadow: 0 4px 18px rgba(17,153,142,.35);
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .send-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(17,153,142,.45); }
        .send-btn:active:not(:disabled) { transform: translateY(0); }
        .send-btn:disabled { opacity: .4; cursor: default; transform: none; box-shadow: none; }
        .send-hint { font-size: 11px; color: var(--text-muted); text-align: center; margin-top: 7px; }

        .sms-overlay {
          position: fixed; inset: 0;
          background: ${isDark ? 'rgba(0,0,0,0.65)' : 'rgba(240, 251, 250, 0.75)'};
          backdrop-filter: blur(3px);
          z-index: 8888;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 18px;
          animation: fadeIn .2s ease;
        }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }

        .sms-spinner {
          width: 54px; height: 54px;
          border: 4px solid var(--teal-mid);
          border-top-color: var(--teal);
          border-radius: 50%;
          animation: spin .75s linear infinite;
          box-shadow: 0 4px 18px rgba(17,153,142,.25);
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .sms-overlay-label { font-size: 14px; font-weight: 700; color: ${isDark ? '#5eead4' : 'var(--teal-dark)'}; letter-spacing: -.1px; }
        .sms-overlay-sub   { font-size: 12px; color: var(--text-muted); margin-top: -10px; }
        .sms-overlay-card {
          background: var(--white); border: 2px solid var(--teal-mid);
          border-radius: 20px; padding: 32px 44px;
          display: flex; flex-direction: column; align-items: center; gap: 14px;
          box-shadow: var(--shadow-lg);
        }

        /* ── Balance Modal ── */
        .bal-backdrop {
          position: fixed; inset: 0;
          background: ${isDark ? 'rgba(0,0,0,0.65)' : 'rgba(15,45,43,.45)'};
          backdrop-filter: blur(4px);
          z-index: 9000;
          display: flex; align-items: center; justify-content: center;
          animation: fadeIn .2s ease;
        }
        .bal-modal {
          background: var(--white); border: 2px solid var(--teal-mid);
          border-radius: 22px; width: 360px; max-width: 95vw;
          box-shadow: var(--shadow-lg); overflow: hidden;
          animation: modalIn .25s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes modalIn { from { opacity:0; transform:scale(.88) translateY(16px); } to { opacity:1; transform:scale(1) translateY(0); } }

        .bal-header {
          background: var(--teal-grad);
          padding: 20px 22px 18px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .bal-header-title { font-size: 15px; font-weight: 800; color: white; letter-spacing: -.3px; display: flex; align-items: center; gap: 8px; }
        .bal-close {
          background: rgba(255,255,255,.2); border: none;
          border-radius: 8px; color: white; cursor: pointer;
          font-size: 18px; width: 30px; height: 30px;
          display: flex; align-items: center; justify-content: center;
          transition: background .15s;
        }
        .bal-close:hover { background: rgba(255,255,255,.35); }

        .bal-body { padding: 24px 22px; }
        .bal-loading { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 12px 0; }
        .bal-spinner { width: 40px; height: 40px; border: 3px solid var(--teal-mid); border-top-color: var(--teal); border-radius: 50%; animation: spin .75s linear infinite; }
        .bal-loading-txt { font-size: 13px; color: var(--text-muted); font-weight: 600; }

        .bal-error {
          display: flex; align-items: center; gap: 10px;
          background: ${isDark ? 'rgba(220,38,38,0.14)' : '#fff5f5'};
          border: 1.5px solid ${isDark ? 'rgba(248,113,113,0.30)' : '#fecaca'};
          border-radius: 10px; padding: 12px 14px;
          color: ${isDark ? '#f87171' : '#b91c1c'}; font-size: 13px; font-weight: 600;
        }

        .bal-card { border: 2px solid var(--teal-mid); border-radius: 14px; overflow: hidden; }
        .bal-card-top { background: var(--teal-soft); padding: 18px 20px 14px; text-align: center; }
        .bal-credit-label { font-size: 11px; color: var(--text-muted); font-weight: 700; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 6px; }
        .bal-credit-val { font-size: 38px; font-weight: 800; font-family: 'JetBrains Mono', monospace; color: var(--teal); line-height: 1.1; }
        .bal-credit-unit { font-size: 14px; color: var(--text-muted); margin-top: 4px; font-weight: 600; }
        .bal-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 18px; border-top: 1px solid var(--border-s); font-size: 12px; }
        .bal-row-key { color: var(--text-muted); font-weight: 600; }
        .bal-row-val { color: var(--text-dark); font-weight: 700; font-family: 'JetBrains Mono', monospace; font-size: 12px; }
        .bal-status-ok  { color: ${isDark ? '#4ade80' : '#16a34a'}; background: ${isDark ? 'rgba(21,128,61,0.18)' : '#dcfce7'}; border-radius: 6px; padding: 2px 9px; }
        .bal-status-err { color: ${isDark ? '#f87171' : '#dc2626'}; background: ${isDark ? 'rgba(220,38,38,0.18)' : '#fee2e2'}; border-radius: 6px; padding: 2px 9px; }

        .bal-refresh-btn {
          width: 100%; margin-top: 16px;
          background: var(--teal-soft); border: 1.5px solid var(--border);
          border-radius: 10px; color: ${isDark ? '#5eead4' : 'var(--teal-dark)'};
          cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px; font-weight: 700; padding: 9px;
          transition: all .2s;
          display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .bal-refresh-btn:hover { background: var(--teal); color: white; border-color: var(--teal); }

        .empty-state { padding: 44px; text-align: center; color: var(--text-muted); }
        .empty-icon  { font-size: 44px; margin-bottom: 10px; }

        .toast {
          position: fixed; bottom: 28px; right: 28px;
          background: var(--white); border: 2px solid;
          border-radius: 14px; padding: 13px 20px;
          font-size: 13px; font-weight: 700;
          box-shadow: var(--shadow-lg);
          display: flex; align-items: center; gap: 9px;
          z-index: 9999; animation: toastIn .3s ease;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
        @keyframes toastIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .toast.ok  { border-color: var(--teal); color: ${isDark ? '#5eead4' : 'var(--teal-dark)'}; }
        .toast.err { border-color: #ef4444; color: ${isDark ? '#f87171' : '#b91c1c'}; }

        /* ── Patient quick-search ── */
        .psearch-wrap {
          position: relative;
          padding: 10px 18px 0;
          border-bottom: 1px solid var(--border-s);
          padding-bottom: 10px;
        }
        .psearch-input { font-size: 12px !important; padding: 7px 11px !important; }
        .psearch-dropdown {
          position: absolute; left: 18px; right: 18px; top: calc(100% - 2px);
          background: var(--white);
          border: 2px solid var(--teal-mid);
          border-radius: 11px;
          box-shadow: var(--shadow-lg);
          z-index: 500;
          overflow: hidden;
          animation: chipIn .15s ease;
        }
        .psearch-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 14px;
          cursor: pointer;
          border-bottom: 1px solid var(--border-s);
          transition: background .12s;
        }
        .psearch-item:last-child { border-bottom: none; }
        .psearch-item:hover { background: var(--teal-soft); }
      `}</style>

      {/* ── Loading Overlay ── */}
      {(loading || sending) && (
        <div className="sms-overlay">
          <div className="sms-overlay-card">
            <div className="sms-spinner" />
            <div className="sms-overlay-label">
              {sending ? '📤 Sending SMS…' : '⏳ Loading Patients…'}
            </div>
            <div className="sms-overlay-sub">
              {sending
                ? `Sending to ${selected.length} patient${selected.length !== 1 ? 's' : ''}. Please wait.`
                : 'Fetching records from the database…'}
            </div>
          </div>
        </div>
      )}

      {/* ── SMS Balance Modal ── */}
      {balanceModal && (
        <div className="bal-backdrop" onClick={() => setBalanceModal(false)}>
          <div className="bal-modal" onClick={e => e.stopPropagation()}>
            <div className="bal-header">
              <div className="bal-header-title">💳 SMS Balance</div>
              <button className="bal-close" onClick={() => setBalanceModal(false)}>✕</button>
            </div>
            <div className="bal-body">
              {balanceLoading ? (
                <div className="bal-loading">
                  <div className="bal-spinner" />
                  <div className="bal-loading-txt">Checking balance…</div>
                </div>
              ) : balanceError ? (
                <div className="bal-error">⚠️ {balanceError}</div>
              ) : balanceData ? (
                <div className="bal-card">
                  <div className="bal-card-top">
                    <div className="bal-credit-label">Available Credits</div>
                    <div className="bal-credit-val">
                      {balanceData.balance ?? balanceData.Balance ?? balanceData.credit ?? '—'}
                    </div>
                    <div className="bal-credit-unit">SMS Credits</div>
                  </div>
                  <div>
                    
                    {balanceData.currency && (
                      <div className="bal-row">
                        <span className="bal-row-key">Currency</span>
                        <span className="bal-row-val">{balanceData.currency}</span>
                      </div>
                    )}
                   
                  </div>
                </div>
              ) : null}

              {!balanceLoading && (
                <button className="bal-refresh-btn" onClick={handleCheckBalance}>
                  🔄 Refresh Balance
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="sms-root">

        {/* ── Header ── */}
        <div className="sms-header">
          <div className="sms-header-left">
            <div className="sms-icon-box">📨</div>
            <div>
              <div className="sms-title">Patient SMS Broadcast</div>
              <div className="sms-subtitle">Send targeted messages to filtered patient groups</div>
            </div>
          </div>

          <div className="sms-header-right">
            <button className="balance-btn" onClick={handleCheckBalance}>
              💳 Check SMS Balance
            </button>
            <div className="sms-stats">
              {[
                { label: 'Loaded',    val: patients.length, cls: ''      },
                { label: 'Selected',  val: selected.length, cls: ''      },
                { label: 'SMS Pages', val: smsPages(msgLen),cls: 'pages' },
                { label: 'Chars',     val: msgLen,          cls: 'chars' },
              ].map(s => (
                <div key={s.label} className={`stat-pill ${s.cls}`}>
                  <div className="stat-pill-val">{s.val}</div>
                  <div className="stat-pill-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="sms-body">

          {/* ════ LEFT ════ */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">👥 Patient Directory</span>
            </div>

            <div className="filter-bar">
              <div style={{ position: 'relative', display: 'flex', gap: 6 }}>
                <input
                  className="sms-input"
                  placeholder="🔍  Search ID, name, phone…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && commitSearch()}
                  style={{ paddingRight: search && search !== appliedSearch ? '38px' : undefined }}
                />
                {/* Show a tiny dot when typed text hasn't been applied yet */}
                {search && search !== appliedSearch && (
                  <span style={{
                    position: 'absolute', right: 82, top: '50%', transform: 'translateY(-50%)',
                    width: 7, height: 7, borderRadius: '50%', background: '#f59e0b'
                  }} title="Press Search to apply" />
                )}
                <button
                  onClick={commitSearch}
                  style={{
                    flexShrink: 0,
                    background: 'var(--teal-grad)',
                    border: 'none', borderRadius: 9,
                    color: 'white', cursor: 'pointer',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12, fontWeight: 700,
                    padding: '0 14px',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(17,153,142,.3)',
                    transition: 'all .2s',
                  }}
                >
                  Search
                </button>
              </div>
              <select className="sms-input" value={fInsur} onChange={e => setFInsur(e.target.value)}>
                <option value="">All Insurance Types</option>
                {insurTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="sms-input" value={fMonth} onChange={e => setFMonth(e.target.value)}>
                <option value="">All Months</option>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select className="sms-input" value={fReligion} onChange={e => setFReligion(e.target.value)}>
                <option value="">All Religions</option>
                {religions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {/* Active filter indicator + reset */}
            {(appliedSearch || fInsur || fMonth || fReligion) && (
              <div style={{ padding: '6px 20px', background: '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#92400e', fontWeight: 600 }}>
                  🔍 Filters active
                  {appliedSearch && <span style={{ marginLeft: 8, background: '#fef3c7', borderRadius: 5, padding: '1px 7px' }}>search: <b>{appliedSearch}</b></span>}
                  {fInsur    && <span style={{ marginLeft: 6, background: '#fef3c7', borderRadius: 5, padding: '1px 7px' }}>insurance: <b>{fInsur}</b></span>}
                  {fMonth    && <span style={{ marginLeft: 6, background: '#fef3c7', borderRadius: 5, padding: '1px 7px' }}>month: <b>{fMonth}</b></span>}
                  {fReligion && <span style={{ marginLeft: 6, background: '#fef3c7', borderRadius: 5, padding: '1px 7px' }}>religion: <b>{fReligion}</b></span>}
                </span>
                <button onClick={resetFilters} style={{ background: 'none', border: '1.5px solid #f59e0b', borderRadius: 7, color: '#92400e', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '3px 10px' }}>
                  ✕ Reset
                </button>
              </div>
            )}

            <div className="bulk-bar">
              <span className="bulk-bar-info">{patients.length} result{patients.length !== 1 ? 's' : ''} found</span>
              <div className="bulk-btns">
                <button className="bulk-btn" onClick={selectPage}>Page</button>
                <button className="bulk-btn" onClick={selectAll}>All</button>
                <button className="bulk-btn danger" onClick={clearAll}>Clear</button>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="patient-table">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}></th>
                    <th>Patient</th>
                    <th>Phone</th>
                    <th>Insurance</th>
                    <th>Religion</th>
                    <th>Month</th>
                  </tr>
                </thead>
                <tbody>
                  {slice.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="empty-state">
                          <div className="empty-icon">🔍</div>
                          <div style={{ fontWeight: 700, color: '#2d6b65', marginBottom: 4 }}>No patients found</div>
                          <div style={{ fontSize: 12 }}>Try adjusting your search or filters</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    slice.map(p => {
                      const sel   = isSelected(p.id);
                      const badge = insuranceBadge(p.insurance, isDark);
                      return (
                        <tr key={p.id} className={`patient-row${sel ? ' sel-row' : ''}`} onClick={() => toggle(p)}>
                          <td>
                            <input type="checkbox" className="check-box" checked={sel}
                              onChange={() => toggle(p)} onClick={e => e.stopPropagation()} />
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                              <div className="avatar" style={{ background: avatarColor(p.fullName) }}>
                                {(p.fullName || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="patient-name">{p.fullName}</div>
                                <div className="patient-id">{p.id}</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="phone-val">{p.tel || '—'}</span></td>
                          <td>
                            <span className="ins-badge" style={{ background: badge.bg, color: badge.color, borderColor: badge.border }}>
                              {p.insurance || 'N/A'}
                            </span>
                          </td>
                          <td>
                            {p.religion
                              ? <span className="rel-badge">{p.religion}</span>
                              : <span style={{ fontSize: 12, color: '#a8d5d2' }}>—</span>
                            }
                          </td>
                          <td style={{ fontSize: 12, color: '#64a09b', fontFamily: "'JetBrains Mono', monospace" }}>
                            {p.reg_month || '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span className="pag-info">
                {patients.length === 0 ? '0' : page * ROWS + 1}–{Math.min((page + 1) * ROWS, patients.length)} of {patients.length}
              </span>
              <div className="pag-btns">
                <button className="pag-btn" onClick={() => setPage(0)} disabled={page === 0}>«</button>
                <button className="pag-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹</button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const start = Math.max(0, Math.min(page - 2, totalPages - 5));
                  const pg = start + i;
                  return (
                    <button key={pg} className={`pag-btn${pg === page ? ' active' : ''}`} onClick={() => setPage(pg)}>
                      {pg + 1}
                    </button>
                  );
                })}
                <button className="pag-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>›</button>
                <button className="pag-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
              </div>
            </div>
          </div>

          {/* ════ RIGHT ════ */}
          <div className="right-col">

            <div className="panel">
              <div className="panel-header">
                <span className="panel-title">✅ Recipients</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#11998e', fontWeight: 800 }}>
                  {selected.length} selected
                </span>
              </div>

              {/* ── Quick patient search ── */}
              <div className="psearch-wrap" ref={pSearchRef}>
                <input
                  className="sms-input psearch-input"
                  placeholder="➕  Search & add a patient…"
                  value={pSearch}
                  onChange={e => { setPSearch(e.target.value); setPDropdown(true); }}
                  onFocus={() => setPDropdown(true)}
                  onBlur={() => setTimeout(() => setPDropdown(false), 180)}
                />
                {pDropdown && pSuggestions.length > 0 && (
                  <div className="psearch-dropdown">
                    {pSuggestions.map(p => (
                      <div
                        key={p.id}
                        className="psearch-item"
                        onMouseDown={() => { toggle(p); setPSearch(''); setPDropdown(false); }}
                      >
                        <div className="avatar" style={{ background: avatarColor(p.fullName), width: 28, height: 28, fontSize: 11, borderRadius: 7, flexShrink: 0 }}>
                          {(p.fullName || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.fullName}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{p.id} · {p.tel}</div>
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 700, flexShrink: 0 }}>Add</span>
                      </div>
                    ))}
                  </div>
                )}
                {pDropdown && pSearch.trim().length >= 1 && pSuggestions.length === 0 && (
                  <div className="psearch-dropdown">
                    <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No unselected patients match</div>
                  </div>
                )}
              </div>

              <div className="chips-area">
                {selected.length === 0 ? (
                  <div className="chips-empty">Search above or tick rows in the table</div>
                ) : (
                  selected.slice(0, 24).map(p => (
                    <div key={p.id} className="chip">
                      {(p.fullName || '').split(' ')[0]}
                      <button className="chip-x" onClick={() => toggle(p)}>×</button>
                    </div>
                  ))
                )}
                {selected.length > 24 && (
                  <div className="chip" style={{ opacity: .65 }}>+{selected.length - 24} more</div>
                )}
              </div>
            </div>

            <div className="panel">
              <div className="panel-header">
                <span className="panel-title">✏️ Compose Message</span>
              </div>
              <div className="compose-area">
                <textarea
                  ref={textRef}
                  className="sms-textarea"
                  placeholder="Type your message here…&#10;&#10;Keep it clear and concise."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={6}
                />
                <div className="char-bar">
                  <span className={charsLeft(msgLen) < 30 ? 'char-warn' : ''}>
                    {msgLen} chars · {charsLeft(msgLen)} remaining
                  </span>
                  <span className="pages">{smsPages(msgLen)} page{smsPages(msgLen) > 1 ? 's' : ''}</span>
                </div>

                <div className="tpl-label">Quick Templates</div>
                <div className="tpl-grid">
                  {TEMPLATES.map(t => (
                    <button key={t.label} className="tpl-btn" onClick={() => setMessage(t.msg)}>{t.label}</button>
                  ))}
                </div>

                <button
                  className="send-btn"
                  onClick={handleSend}
                  disabled={sending || selected.length === 0 || !message.trim()}
                >
                  {sending ? <>⏳ Sending…</> : <>📤 Send to {selected.length} Patient{selected.length !== 1 ? 's' : ''}</>}
                </button>
                {(selected.length === 0 || !message.trim()) && (
                  <div className="send-hint">
                    {selected.length === 0 ? 'Select patients from the table first' : 'Type a message above to send'}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {toast && (
        <div className={`toast ${toast.ok ? 'ok' : 'err'}`}>
          {toast.ok ? '✅' : '❌'} {toast.msg}
        </div>
      )}
    </>
  );
}